import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { tournamentAPI } from '../services/api';
import './TournamentSetup.css';

// Convert 12-hour format to 24-hour for HTML time input
const convertTo24Hour = (time12h) => {
  if (!time12h) return '07:03';
  const [time, period] = time12h.split(' ');
  let [hours, minutes] = time.split(':');
  hours = parseInt(hours);

  if (period === 'PM' && hours !== 12) hours += 12;
  if (period === 'AM' && hours === 12) hours = 0;

  return `${hours.toString().padStart(2, '0')}:${minutes}`;
};

// Convert 24-hour format to 12-hour for display
const convertTo12Hour = (time24h) => {
  if (!time24h) return '7:03 AM';
  let [hours, minutes] = time24h.split(':');
  hours = parseInt(hours);

  const period = hours >= 12 ? 'PM' : 'AM';
  if (hours > 12) hours -= 12;
  if (hours === 0) hours = 12;

  return `${hours}:${minutes} ${period}`;
};

function TournamentSetup() {
  const navigate = useNavigate();
  const location = useLocation();

  // Check if we're creating from a calendar event
  const fromEvent = location.state?.fromEvent;

  const [tournamentName, setTournamentName] = useState('');
  const [startTime, setStartTime] = useState('7:03 AM');
  const [tournamentDate, setTournamentDate] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Pre-populate form if coming from a calendar event
  useEffect(() => {
    if (fromEvent) {
      if (fromEvent.name) {
        setTournamentName(fromEvent.name);
      }
      if (fromEvent.startTime) {
        setStartTime(fromEvent.startTime);
      }
      if (fromEvent.date) {
        // Format date for input field (YYYY-MM-DD)
        const eventDate = new Date(fromEvent.date);
        const formattedDate = eventDate.toISOString().split('T')[0];
        setTournamentDate(formattedDate);
      }
    }
  }, [fromEvent]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!tournamentName.trim()) return;

    try {
      setIsLoading(true);
      setError(null);

      // Build tournament data
      const tournamentData = {
        name: tournamentName.trim(),
        startTime: startTime,
        status: 'setup'
      };

      // Add date if provided
      if (tournamentDate) {
        tournamentData.date = new Date(tournamentDate);
      }

      // Add seasonEventId if creating from a calendar event
      if (fromEvent?.seasonEventId) {
        tournamentData.seasonEventId = fromEvent.seasonEventId;
      }

      // Create tournament with basic info
      const tournament = await tournamentAPI.create(tournamentData);

      const tournamentId = tournament.data._id;

      // Navigate to data import step
      navigate(`/tournament/${tournamentId}/import`);

    } catch (error) {
      console.error('Error creating tournament:', error);
      setError(error.message || 'Failed to create tournament');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="tournament-setup">
      <div className="setup-container">
        <header className="setup-header">
          <h1>Create New Tournament</h1>
          <p>Step 1: Enter basic tournament information</p>
        </header>

        {fromEvent && (
          <div className="linked-event-notice" style={{
            background: '#e8f5e9',
            border: '1px solid #4caf50',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px'
          }}>
            <h4 style={{ margin: '0 0 8px 0', color: '#2e7d32' }}>
              Creating from Calendar Event
            </h4>
            <p style={{ margin: 0, color: '#1b5e20' }}>
              This tournament will be linked to the calendar event.
              {fromEvent.expectedPlayers > 0 && (
                <strong> {fromEvent.expectedPlayers} signups will be transferred as players.</strong>
              )}
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="setup-form">
          <div className="form-group">
            <label htmlFor="tournamentName">Tournament Name *</label>
            <input
              type="text"
              id="tournamentName"
              value={tournamentName}
              onChange={(e) => setTournamentName(e.target.value)}
              placeholder="Enter tournament name"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="tournamentDate">Tournament Date</label>
            <input
              type="date"
              id="tournamentDate"
              value={tournamentDate}
              onChange={(e) => setTournamentDate(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label htmlFor="startTime">Start Time *</label>
            <input
              type="time"
              id="startTime"
              value={convertTo24Hour(startTime)}
              onChange={(e) => setStartTime(convertTo12Hour(e.target.value))}
              required
            />
            <small>Tournament tee times will begin at this time</small>
          </div>

          <div className="info-section">
            <h3>Next Steps</h3>
            <p>After creating your tournament, you'll be able to:</p>
            <ul>
              {fromEvent ? (
                <li>Players from calendar event signups will be auto-imported</li>
              ) : (
                <li>Import player data from Excel files</li>
              )}
              <li>Review and validate imported data</li>
              <li>Configure tournament settings</li>
              <li>Generate tee sheet assignments</li>
            </ul>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-actions">
            <button 
              type="submit" 
              disabled={isLoading || !tournamentName.trim()}
              className="btn btn-primary"
            >
{isLoading ? '⏳ Creating...' : 'Next: Import Data'}
            </button>
            
            <button 
              type="button" 
              onClick={() => navigate('/')}
              className="btn btn-outline"
            >
              Cancel
            </button>
          </div>
        </form>

      </div>
    </div>
  );
}

export default TournamentSetup;